package org.company.app.presentation.component.table


import androidx.compose.foundation.*
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.wrapContentSize
import androidx.compose.foundation.shape.CornerSize
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.snapshots.SnapshotStateMap
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.company.app.presentation.component.ZiFocusTextField
import org.company.app.presentation.ui.carddetails.AutoSubmittingTextField

@Composable
fun Table(
    modifier: Modifier = Modifier,
    rowCount: Int,
    columnCount: Int,
    stickyRowCount: Int = 0,
    stickyColumnCount: Int = 0,
    maxCellWidthDp: Dp = Dp.Infinity,
    maxCellHeightDp: Dp = Dp.Infinity,
    verticalScrollState: ScrollState = rememberScrollState(),
    horizontalScrollState: ScrollState = rememberScrollState(),
    customHeaders: List<String> = emptyList(),
    customElements: List<@Composable () -> Unit> = emptyList(),
    cellContent: @Composable (rowIndex: Int, columnIndex: Int, customHeaders: List<String>, customElements: List<@Composable () -> Unit>) -> Unit
) {
    val columnWidths = remember { mutableStateMapOf<Int, Int>() }
    val rowHeights = remember { mutableStateMapOf<Int, Int>() }

    val maxCellWidth = if (listOf(Dp.Infinity, Dp.Unspecified).contains(maxCellWidthDp)) {
        Constraints.Infinity
    } else {
        with(LocalDensity.current) { maxCellWidthDp.toPx() }.toInt()
    }
    val maxCellHeight = if (listOf(Dp.Infinity, Dp.Unspecified).contains(maxCellHeightDp)) {
        Constraints.Infinity
    } else {
        with(LocalDensity.current) { maxCellHeightDp.toPx() }.toInt()
    }

    var accumWidths = mutableListOf<Int>()
    var accumHeights = mutableListOf<Int>()

    @Composable
    fun StickyCells(modifier: Modifier = Modifier, rowCount: Int, columnCount: Int) {
        if (rowCount > 0 && columnCount > 0) {
            Box(modifier = modifier) {
                Layout(
                    content = {
                        (0 until rowCount).forEach { rowIndex ->
                            (0 until columnCount).forEach { columnIndex ->
                                cellContent( rowIndex, columnIndex,customHeaders, customElements)
                            }
                        }
                    },
                ) { measurables, constraints ->
                    val placeables = measurables.mapIndexed { index, it ->
                        val columnIndex = index % columnCount
                        val rowIndex = index / columnCount
                        it.measure(
                            Constraints(
                                minWidth = columnWidths[columnIndex] ?: 0,
                                minHeight = rowHeights[rowIndex] ?: 0,
                                maxWidth = columnWidths[columnIndex] ?: 0,
                                maxHeight = rowHeights[rowIndex] ?: 0
                            )
                        )
                    }

                    val totalWidth = accumWidths[columnCount]
                    val totalHeight = accumHeights[rowCount]

                    layout(width = totalWidth, height = totalHeight) {
                        placeables.forEachIndexed { index, placeable ->
                            val columnIndex = index % columnCount
                            val rowIndex = index / columnCount

                            placeable.placeRelative(
                                accumWidths[columnIndex],
                                accumHeights[rowIndex]
                            )
                        }
                    }
                }
            }
        }
    }

    Box(modifier = modifier) {
        Box(
            modifier = Modifier
                .then(Modifier.horizontalScroll(horizontalScrollState))
                .then(Modifier.verticalScroll(verticalScrollState))
        ) {
            Layout(
                content = {
                    (0 until rowCount).forEach { rowIndex ->
                        (0 until columnCount).forEach { columnIndex ->
                            cellContent(rowIndex, columnIndex,customHeaders, customElements)
                        }
                    }
                },
            ) { measurables, constraints ->
                val placeables = measurables.mapIndexed { index, it ->
                    val columnIndex = index % columnCount
                    val rowIndex = index / columnCount
                    it.measure(
                        Constraints(
                            minWidth = columnWidths[columnIndex] ?: 0,
                            minHeight = rowHeights[rowIndex] ?: 0,
                            maxWidth = maxCellWidth,
                            maxHeight = maxCellHeight
                        )
                    )
                }

                placeables.forEachIndexed { index, placeable ->
                    val columnIndex = index % columnCount
                    val rowIndex = index / columnCount

                    val existingWidth = columnWidths[columnIndex] ?: 0
                    val maxWidth = maxOf(existingWidth, placeable.width)
                    if (maxWidth > existingWidth) {
                        columnWidths[columnIndex] = maxWidth
                    }

                    val existingHeight = rowHeights[rowIndex] ?: 0
                    val maxHeight = maxOf(existingHeight, placeable.height)
                    if (maxHeight > existingHeight) {
                        rowHeights[rowIndex] = maxHeight
                    }
                }

                accumWidths = mutableListOf(0).apply {
                    (1..columnWidths.size).forEach { i ->
                        this += this.last() + columnWidths[i - 1]!!
                    }
                }
                accumHeights = mutableListOf(0).apply {
                    (1..rowHeights.size).forEach { i ->
                        this += this.last() + rowHeights[i - 1]!!
                    }
                }

                val totalWidth = accumWidths.last()
                val totalHeight = accumHeights.last()

                layout(width = totalWidth, height = totalHeight) {
                    placeables.forEachIndexed { index, placeable ->
                        val columnIndex = index % columnCount
                        val rowIndex = index / columnCount

                        placeable.placeRelative(accumWidths[columnIndex], accumHeights[rowIndex])
                    }
                }
            }
        }

        StickyCells(
            modifier = Modifier.horizontalScroll(horizontalScrollState),
            rowCount = stickyRowCount,
            columnCount = columnCount
        )

        StickyCells(
            modifier = Modifier.verticalScroll(verticalScrollState),
            rowCount = rowCount,
            columnCount = stickyColumnCount
        )

        StickyCells(
            rowCount = stickyRowCount,
            columnCount = stickyColumnCount
        )
    }
}

@Composable
fun HeaderCell(text: String) {
    if (text.isNullOrBlank()) {
        androidx.compose.material.Surface {}
        return
    }
    Box(
        modifier = Modifier
            .background(
                color = Color.White,
                shape = RoundedCornerShape(corner = CornerSize(0.dp))
            )
            .border(width = 1.dp, color = Color.Gray)
    ) {
        androidx.compose.material.Text(
            text = text,
            fontWeight = FontWeight.Bold,
            modifier = Modifier
                .padding(10.dp)
                .align(Alignment.Center)
        )
    }
}

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun ContentCell(text: MutableState<String?>, isChecked: Boolean, alignment: Alignment = Alignment.Center,onChange:(String?)->Unit={}) {
    Box(modifier = Modifier
        .background(
            color = if (!isChecked) Color.White else Color.Yellow,
            shape = RoundedCornerShape(corner = CornerSize(0.dp))
        )
        .border(width = 1.dp, color = Color.Gray)
        .clickable {
            // do something wonderful
        }
    ) {
        if (text.value==""){
            Text("Type here",
                    style = MaterialTheme.typography.bodySmall.copy(fontSize = 15.sp, fontFamily = MaterialTheme.typography.bodySmall.fontFamily,color = Color(0xFF03264A), )
                , color = Color.Gray, modifier = Modifier.padding(start = 16.dp).align(Alignment.CenterStart))
        }
        AutoSubmittingTextField(text,
            textStyle = MaterialTheme.typography.bodySmall.copy(fontSize = 15.sp, fontFamily = MaterialTheme.typography.bodySmall.fontFamily,color = Color(0xFF03264A), )
            , onClick = {
                onChange(text.value)
        }, modifier = Modifier.align(alignment).padding(start = 16.dp))
        /*
        ZiFocusTextField(text, placeHolder = "Type Here",modifier = Modifier
            .padding(10.dp),
            withDivider = false,
            textStyle = MaterialTheme.typography.bodySmall.copy(fontSize = 15.sp, fontFamily = MaterialTheme.typography.bodySmall.fontFamily,color = Color(0xFF03264A), textAlign = TextAlign.Center)
        ) */

       /* androidx.compose.material.Text(
            text = text.value,
            modifier = Modifier
                .padding(10.dp)
                .align(alignment)
        ) */
    }
}
@Composable
fun ContentCell(text: String, isChecked: Boolean, alignment: Alignment = Alignment.Center) {
    Box(modifier = Modifier
        .background(
            color = if (!isChecked) Color.White else Color.Yellow,
            shape = RoundedCornerShape(corner = CornerSize(0.dp))
        )
        .border(width = 1.dp, color = Color.Gray)
        .clickable {
            // do something wonderful
        }
    ) {

         androidx.compose.material.Text(
             text = text,
             modifier = Modifier
                 .padding(10.dp)
                 .align(alignment)
         )
    }
}

@Composable
fun ToggleCell(row: Int, checkedRows: SnapshotStateMap<Int, Unit>) {
    Box(contentAlignment = Alignment.Center, modifier = Modifier.background(Color.White)) {
        androidx.compose.material.Switch(
            checked = checkedRows.containsKey(row),
            onCheckedChange = {
                if (!checkedRows.containsKey(row)) {
                    checkedRows[row] = Unit
                } else {
                    checkedRows.remove(row)
                }
            }
        )
    }
}
